import type { EpcProjectMembershipRow, EpcProjectOrgUnit } from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { orgUnitKey, taskOrgUnitId } from './epcScheduleDepartmentAssignment';

export type DeptCoverageSegment = { orgId: string; name: string; count: number; pct: number };

export type DeptCoverage = {
  total: number;
  assigned: number;
  unassigned: number;
  coveragePct: number;
  /** Active org units with at least one line, count desc. `pct` is the share of ALL lines (stacked bar). */
  segments: DeptCoverageSegment[];
};

export function computeDeptCoverage(
  tasks: EpcScheduleTaskRow[],
  orgUnits: EpcProjectOrgUnit[]
): DeptCoverage {
  const counts = new Map<string, number>();
  let assigned = 0;
  for (const t of tasks) {
    const oid = taskOrgUnitId(t);
    if (!oid) continue;
    assigned += 1;
    counts.set(oid, (counts.get(oid) ?? 0) + 1);
  }
  const total = tasks.length;
  const segments: DeptCoverageSegment[] = [];
  for (const u of orgUnits) {
    if (u.inactive) continue;
    const id = orgUnitKey(u);
    const count = counts.get(id) ?? 0;
    if (count === 0) continue;
    segments.push({ orgId: id, name: u.name, count, pct: Math.round((count / total) * 100) });
  }
  segments.sort((a, b) => b.count - a.count);
  return {
    total,
    assigned,
    unassigned: total - assigned,
    coveragePct: total === 0 ? 0 : Math.round((assigned / total) * 100),
    segments,
  };
}

/** Department's share of all assigned lines (load bar on the target card). */
export function deptLoadPct(coverage: DeptCoverage, orgId: string): number {
  if (coverage.assigned === 0) return 0;
  const seg = coverage.segments.find((s) => s.orgId === orgId);
  return seg ? Math.round((seg.count / coverage.assigned) * 100) : 0;
}

/** Active members (any role) attached to this org unit. */
export function deptTeamSize(memberships: EpcProjectMembershipRow[], orgId: string): number {
  return memberships.filter((m) => !m.inactive && m.orgUnitId === orgId).length;
}
